import type { BlockTestimonials } from 'types/block-testimonials';

import React from 'react';

import { Paragraph, Wrap } from '@leafwell/components';

import { getRandomInt } from 'lib/utils/generateRandomInt';

import HeadingBody from './heading-body';
import Figure from './figure';

const Testimonial: React.FC<
  BlockTestimonials['testimonials'][0] & { textColor?: string }
> = ({ testimonial, profilePicture, name, position, textColor }) => {
  return (
    <li className="grid gap-y-4 justify-items-center">
      <Figure
        {...profilePicture}
        className="w-[150px] h-[150px]"
        classNameImage="rounded-32px"
      />
      <div className="grid gap-y-2.5">
        <Paragraph
          className={['text-center max-w-xs', textColor].join(' ')}
          dangerouslySetInnerHTML={{ __html: testimonial }}
        />
        <div className="grid gap-y-1">
          <div
            className={[
              'mx-auto w-8 h-1.5',
              textColor === 'text-white' ? 'bg-white' : 'bg-lilac-500',
            ].join(' ')}
          ></div>
          <Paragraph
            size="small"
            className={[
              'text-center',
              textColor === 'text-white' ? 'text-white' : 'text-lilac-500',
            ].join(' ')}
          >
            {name}
          </Paragraph>
          {position ? (
            <Paragraph
              size="small"
              className={[
                'text-center',
                textColor === 'text-white'
                  ? 'text-white'
                  : 'text-lilac-300 -mt-1',
              ].join(' ')}
            >
              {position}
            </Paragraph>
          ) : null}
        </div>
      </div>
    </li>
  );
};

const Testimonials: React.FC<BlockTestimonials> = ({
  sectionId,
  title,
  description,
  backgroundColor,
  testimonials,
}) => {
  const isSingle = testimonials?.length == 1;
  return (
    <section
      id={sectionId}
      className={[
        backgroundColor === 'green'
          ? 'bg-dark-green-gradient my-12 md:my-16'
          : 'bg-white',
      ].join(' ')}
    >
      <Wrap>
        {title || description ? (
          <div className="grid gap-y-6 justify-items-center">
            {title && (
              <HeadingBody
                level="2"
                tagName="h2"
                title={title}
                className={[
                  'font-semibold text-center',
                  backgroundColor === 'green' ? 'text-white' : 'text-black',
                ].join(' ')}
              />
            )}
            {description && (
              <HeadingBody
                level="6"
                tagName="p"
                title={description}
                className={[
                  'text-center lg:max-w-3xl',
                  backgroundColor === 'green' ? 'text-white' : 'text-gray-700',
                ].join(' ')}
              />
            )}
          </div>
        ) : null}
        {testimonials?.length > 0 ? (
          isSingle ? (
            <div className="grid lg:grid-cols-auto-auto lg:items-center lg:gap-x-20 gap-y-4">
              <Figure
                {...testimonials[0]?.profilePicture}
                className="justify-self-center lg:justify-self-end"
                classNameImage="rounded-32px"
              />
              <div className="grid gap-y-2.5">
                <Paragraph
                  size="large"
                  className={[
                    'text-center lg:text-left max-w-md',
                    backgroundColor === 'green' ? 'text-white' : 'text-black',
                  ].join(' ')}
                  dangerouslySetInnerHTML={{
                    __html: testimonials[0]?.testimonial,
                  }}
                />
                <div className="grid gap-y-1">
                  <div
                    className={[
                      'mx-auto lg:mx-0 w-8 h-1.5',
                      backgroundColor === 'green' ? 'bg-white' : 'bg-lilac-500',
                    ].join(' ')}
                  ></div>
                  <Paragraph
                    className={[
                      'text-center lg:text-left',
                      backgroundColor === 'green'
                        ? 'text-white'
                        : 'text-lilac-500',
                    ].join(' ')}
                  >
                    {testimonials[0]?.name}
                    {testimonials[0]?.position
                      ? `, ${testimonials[0]?.position}`
                      : null}
                  </Paragraph>
                </div>
              </div>
            </div>
          ) : (
            <ul className="flex flex-wrap justify-between gap-y-6">
              {testimonials.map(
                ({ testimonial, profilePicture, name, position }) => {
                  const idx = getRandomInt(1, 1000000);
                  return (
                    <Testimonial
                      key={`${idx}-testimonial-card`}
                      testimonial={testimonial}
                      profilePicture={profilePicture}
                      name={name}
                      position={position}
                      textColor={
                        backgroundColor === 'green'
                          ? 'text-white'
                          : 'text-black'
                      }
                    />
                  );
                },
              )}
            </ul>
          )
        ) : null}
      </Wrap>
    </section>
  );
};

export default Testimonials;
